import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  LayoutDashboard,
  FolderKanban,
  Users,
  Truck,
  Package,
  DollarSign,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  UserCog,
  Package2,
  Boxes,
  FileText,
  Settings2,
  Wrench,
  HelpCircle,
  Cog,
  Calendar,
  CalendarPlus,
  ClipboardList,
  RotateCcw,
  Database,
  ClipboardCheck,
  Clock,
  Tags,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useFeatures } from "../../context/FeatureContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Map of menu item keys to their paths
const MENU_ITEM_PATH_MAP = {
  dashboard: "/dashboard",
  my_assignments: "/my-assignments",
  job_history: "/job-history",
  projects: "/projects",
  operations: "/operations",
  suppliers: "/suppliers",
  purchase_orders: "/purchase-orders",
  materials: "/materials",
  inventory: "/inventory",
  standard_items: "/standard-items",
  team: "/team",
  expenses: "/expenses",
  services: "/services",
  installation_systems: "/installation-systems",
  customers: "/customers",
  service_calendar: "/service-calendar",
  rental_items: "/rental-items",
  rental_management: "/rental-management",
  user_management: "/users",
  settings: "/settings",
};

const getNavItems = (userRole, features) => {
  const { isSectionEnabled, isMenuItemEnabled, isModuleEnabled } = features;
  
  // Customer-specific navigation
  if (userRole === "customer") {
    const customerItems = [];
    
    customerItems.push({ type: "divider", label: "Overview" });
    customerItems.push({ path: "/customer-dashboard", label: "Dashboard", icon: LayoutDashboard });
    
    customerItems.push({ type: "divider", label: "My Account" });
    customerItems.push({ path: "/my-profile", label: "My Profile", icon: UserCog });
    customerItems.push({ path: "/my-installations", label: "My Installations", icon: Cog });
    
    customerItems.push({ type: "divider", label: "Services" });
    
    // Check if service booking module is enabled
    if (isModuleEnabled("service_booking", userRole)) {
      customerItems.push({ path: "/my-bookings", label: "Book Service", icon: CalendarPlus });
      customerItems.push({ path: "/my-job-cards", label: "My Job Cards", icon: ClipboardCheck });
    }
    
    // Check if rental system module is enabled
    if (isModuleEnabled("rental_system", userRole)) {
      customerItems.push({ path: "/customer/rentals", label: "My Rentals", icon: Package });
    }
    
    return customerItems;
  }

  // Admin/Manager/Worker navigation with grouped sections
  const items = [];
  
  // Overview section
  if (isSectionEnabled("overview", userRole)) {
    items.push({ type: "divider", label: "Overview", sectionKey: "overview" });
    if (isMenuItemEnabled("dashboard", userRole)) {
      items.push({ path: "/dashboard", label: "Dashboard", icon: LayoutDashboard, itemKey: "dashboard" });
    }
    if (isMenuItemEnabled("my_assignments", userRole)) {
      items.push({ path: "/my-assignments", label: "My Assignments", icon: ClipboardList, itemKey: "my_assignments" });
    }
    // Job History - view completed job cards
    if (isMenuItemEnabled("job_history", userRole)) {
      items.push({ path: "/job-history", label: "Job History", icon: ClipboardCheck, itemKey: "job_history" });
    }
    // Pending Approvals - for admin/manager/workers to approve booking requests
    if (isModuleEnabled("service_booking", userRole) && isMenuItemEnabled("pending_approvals", userRole)) {
      items.push({ path: "/pending-approvals", label: "Pending Approvals", icon: Clock, itemKey: "pending_approvals", showBadge: true });
    }
  }
  
  // Manufacturing section (Projects, Parts, Part Categories, Standard Items, Labour)
  if (isSectionEnabled("resources", userRole) || isSectionEnabled("projects", userRole)) {
    items.push({ type: "divider", label: "Manufacturing", sectionKey: "manufacturing" });
    if (isMenuItemEnabled("projects", userRole)) {
      items.push({ path: "/projects", label: "Projects", icon: FolderKanban, itemKey: "projects" });
    }
    if (isMenuItemEnabled("parts", userRole)) {
      items.push({ path: "/parts", label: "Parts", icon: Cog, itemKey: "parts" });
    }
    if (isMenuItemEnabled("part_categories", userRole)) {
      items.push({ path: "/part-categories", label: "Part Categories", icon: Tags, itemKey: "part_categories" });
    }
    if (isMenuItemEnabled("labour", userRole)) {
      items.push({ path: "/labour", label: "Labour", icon: Users, itemKey: "labour" });
    }
    if (isMenuItemEnabled("standard_items", userRole)) {
      items.push({ path: "/standard-items", label: "Standard Items", icon: Package2, itemKey: "standard_items" });
    }
  }
  
  // Procurement section
  if (isSectionEnabled("procurement", userRole)) {
    items.push({ type: "divider", label: "Procurement", sectionKey: "procurement" });
    if (isMenuItemEnabled("suppliers", userRole)) {
      items.push({ path: "/suppliers", label: "Suppliers", icon: Truck, itemKey: "suppliers" });
    }
    if (isMenuItemEnabled("purchase_orders", userRole) && isModuleEnabled("purchase_orders", userRole)) {
      items.push({ path: "/purchase-orders", label: "Purchase Orders", icon: FileText, itemKey: "purchase_orders" });
    }
    if (isMenuItemEnabled("materials", userRole)) {
      items.push({ path: "/materials", label: "Materials", icon: Package, itemKey: "materials" });
    }
    if (isMenuItemEnabled("inventory", userRole)) {
      items.push({ path: "/inventory", label: "Inventory", icon: Boxes, itemKey: "inventory" });
    }
  }
  
  // Resources section
  if (isSectionEnabled("resources", userRole)) {
    items.push({ type: "divider", label: "Resources", sectionKey: "resources" });
    if (isMenuItemEnabled("team", userRole)) {
      items.push({ path: "/team", label: "Team", icon: Users, itemKey: "team" });
    }
  }
  
  // Finance section
  if (isSectionEnabled("finance", userRole)) {
    items.push({ type: "divider", label: "Finance", sectionKey: "finance" });
    if (isMenuItemEnabled("expenses", userRole)) {
      items.push({ path: "/expenses", label: "Expenses", icon: DollarSign, itemKey: "expenses" });
    }
  }
  
  // Customer Service section - available for admin and workers/managers with permission
  // Order: Customers, Installation Systems, Services, Service Calendar, Add Customer Service
  if (isSectionEnabled("customer_service", userRole)) {
    items.push({ type: "divider", label: "Customer Service", sectionKey: "customer_service" });
    if (isMenuItemEnabled("customers", userRole)) {
      items.push({ path: "/customers", label: "Customers", icon: Users, itemKey: "customers" });
    }
    if (isMenuItemEnabled("installation_systems", userRole)) {
      items.push({ path: "/installation-systems", label: "Installation Systems", icon: Cog, itemKey: "installation_systems" });
    }
    if (isMenuItemEnabled("services", userRole)) {
      items.push({ path: "/services", label: "Services", icon: Wrench, itemKey: "services" });
    }
    if (isMenuItemEnabled("service_calendar", userRole) && isModuleEnabled("service_booking", userRole)) {
      items.push({ path: "/service-calendar", label: "Service Calendar", icon: Calendar, itemKey: "service_calendar" });
    }
    // Add Customer Service - for staff to book services for customers
    if (isModuleEnabled("service_booking", userRole) && isMenuItemEnabled("add_customer_service", userRole)) {
      items.push({ path: "/add-customer-service", label: "Add Customer Service", icon: CalendarPlus, itemKey: "add_customer_service" });
    }
  }
  
  // Rentals section - available for admin and workers/managers with permission
  if (isSectionEnabled("rentals", userRole) && isModuleEnabled("rental_system", userRole)) {
    items.push({ type: "divider", label: "Rentals", sectionKey: "rentals" });
    if (isMenuItemEnabled("rental_management", userRole)) {
      items.push({ path: "/rental-management", label: "Rental Management", icon: RotateCcw, itemKey: "rental_management" });
    }
    if (isMenuItemEnabled("rental_items", userRole)) {
      items.push({ path: "/rental-items", label: "Rental Items", icon: Package, itemKey: "rental_items" });
    }
  }
  
  // Admin section - only for admins
  if (userRole === "admin" && isSectionEnabled("admin", userRole)) {
    items.push({ type: "divider", label: "Admin", sectionKey: "admin" });
    if (isMenuItemEnabled("user_management", userRole)) {
      items.push({ path: "/users", label: "User Management", icon: UserCog, itemKey: "user_management" });
    }
    if (isMenuItemEnabled("settings", userRole)) {
      items.push({ path: "/settings", label: "Settings", icon: Settings2, itemKey: "settings" });
    }
    // Data Management is always available for admins in admin section
    items.push({ path: "/data-management", label: "Data Management", icon: Database });
    // Job Card Template for admin preview
    items.push({ path: "/job-card-template", label: "Job Card Template", icon: FileText, itemKey: "job_card_template" });
  }
  
  // Help is only available for admins
  if (userRole === "admin") {
    items.push({ path: "/help", label: "Help", icon: HelpCircle });
  }
  
  // Filter out empty sections (dividers with no items after them)
  const filteredItems = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.type === "divider") {
      // Look ahead to see if there are any items before the next divider
      let hasItems = false;
      for (let j = i + 1; j < items.length; j++) {
        if (items[j].type === "divider") break;
        hasItems = true;
        break;
      }
      if (hasItems) {
        filteredItems.push(item);
      }
    } else {
      filteredItems.push(item);
    }
  }
  
  return filteredItems;
};

const AppLayout = ({ children, user }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const features = useFeatures();
  
  const navItems = getNavItems(user?.role, features);

  // Poll pending approvals count via lightweight /pending-counts endpoint.
  // This replaces 3 full-collection fetches (which transfer large payloads and
  // exhaust MongoDB Atlas connections) with a single server-side count query.
  useEffect(() => {
    const fetchPendingCount = async () => {
      if (user?.role === "customer") return;
      try {
        const res = await axios.get(`${API}/pending-counts`, { withCredentials: true });
        setPendingApprovalsCount(res.data?.total || 0);
      } catch (error) {
        console.error("Error fetching pending count:", error);
      }
    };
    
    fetchPendingCount();
    const interval = setInterval(fetchPendingCount, 60000);
    return () => clearInterval(interval);
  }, [user?.role]);

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    toast.success("You have been logged out successfully.", { duration: 3000 });
    navigate("/login", { replace: true });
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "admin":
        return "text-red-600";
      case "manager":
        return "text-blue-600";
      default:
        return "text-slate-500";
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="app-layout">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-slate-200 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-slate-200">
            <Link to="/dashboard" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-slate-900 rounded-sm flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
              </div>
              <span className="text-lg font-semibold text-slate-900">
                TaskForge
              </span>
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* User section — moved to top */}
          <div className="p-4 border-b border-slate-200">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-3 w-full p-2 rounded-sm hover:bg-slate-50 transition-colors"
                  data-testid="user-menu-trigger"
                >
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-slate-200 text-slate-700 text-sm">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {user?.name || "User"}
                    </p>
                    <p className={`text-xs capitalize ${getRoleBadgeColor(user?.role)}`}>
                      {user?.role === "worker" ? "Technician" : user?.role || "Member"}
                    </p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-red-600 focus:text-red-600"
                  data-testid="logout-btn"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {navItems.map((item, index) => {
              // Render divider
              if (item.type === "divider") {
                return (
                  <div key={`divider-${index}`} className="pt-4 pb-2">
                    <p className="px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      {item.label}
                    </p>
                  </div>
                );
              }

              const isActive = location.pathname === item.path || 
                (item.path !== "/dashboard" && item.path !== "/customer-dashboard" && location.pathname.startsWith(item.path));
              const Icon = item.icon;
              
              // Check if this item should show a badge
              const showBadge = item.showBadge && item.path === "/pending-approvals" && pendingApprovalsCount > 0;

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-colors duration-150 ${
                    isActive
                      ? "bg-slate-100 text-slate-900"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="h-5 w-5" />
                  <span className="flex-1">{item.label}</span>
                  {showBadge && (
                    <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold text-white bg-amber-500 rounded-full">
                      {pendingApprovalsCount > 99 ? "99+" : pendingApprovalsCount}
                    </span>
                  )}
                  {isActive && !showBadge && (
                    <ChevronRight className="h-4 w-4 ml-auto text-slate-400" />
                  )}
                </Link>
              );
            })}
          </nav>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Mobile header */}
        <header className="sticky top-0 z-30 flex items-center h-16 px-4 bg-white border-b border-slate-200 lg:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <span className="ml-3 text-lg font-semibold text-slate-900">
            TaskForge
          </span>
        </header>

        {/* Page content */}
        <main className="min-h-[calc(100vh-4rem)] lg:min-h-screen">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
